import { useApolloClient } from '@apollo/client'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { CardElement, IbanElement, IdealBankElement, useElements, useStripe } from '@stripe/react-stripe-js'

import useAmplitude from '@eversports/amplitude-react/useAmplitude'
import {
  PaymentAbortOrderDocument,
  PaymentAbortOrderMutation,
  PaymentAbortOrderMutationVariables,
  PaymentMethodType,
  PaymentOrderDocument,
  PaymentOrderQuery,
  usePaymentGetMarketplaceFeeLazyQuery,
  usePaymentTrackCheckoutCompleteMutation,
} from '../../graphql'
import useCheckoutContext from '../../data/useCheckoutContext'
import useOrder from '../../data/useOrder'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import { useIntl } from '../../localization/react'
import { OrderState } from '../../typings/Order'
import {
  CustomPaymentMethodType,
  isStoredCreditCard,
  isSupportedPaymentMethodType,
  OFFLINE_PAYMENT_METHOD,
  PaymentMethod,
  PaymentMethodVerificationMethod,
  ProcessPaymentOptions,
  SelectedPaymentMethod,
} from '../../typings/Payment'

import { BlockingError } from '../../lib/custom-errors'
import useMutationWithErrorHandling from '../../data/useMutationWithErrorHandling'
import getUserTrackingPreferences from '../../data/get-user-tracking-preferences'

import checkIfAdvertisingTrackingAllowed from '../../lib/check-if-advertising-tracking-allowed'
import handlePaymentError from './handle-payment-error'
import PaymentForm from './PaymentForm'
import pollPayment from './poll-payment'
import processPayment from './process-payment'
import confirmOrder from './confirm-order'
import SkeletonPayment from './Skeletons/SkeletonPayment'
import useGooglePurcahseTracking from './hooks/useGooglePurcahseTracking'

const Payment = () => {
  const intl = useIntl()
  const stripe = useStripe()
  const history = useHistory()
  const location = useLocation()
  const context = useCheckoutContext()
  const apolloClient = useApolloClient()
  const elements = useElements()
  const { amplitude, deviceId, sessionId } = useAmplitude()

  const { orderId, paymentInProgress, lastPaymentAttempt = null } = getPhoenixUrlParams(location.search)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<SelectedPaymentMethod | undefined>()
  const [verifyWith, setVerifyWith] = useState<PaymentMethodVerificationMethod | null>(null)
  const [paymentMethods, setPaymentMethods] = useState<Array<PaymentMethod>>([])
  const { orderData, isOrderLoading } = useOrder<PaymentOrderQuery>(PaymentOrderDocument)
  const [paymentWasSuccessful, setPaymentWasSuccessful] = useState(false)
  const [hasHandledGoogleTracking, setHasHandledGoogleTracking] = useState(false)

  const isAdvertisingTrackingAllowed = checkIfAdvertisingTrackingAllowed()

  const [abortOrder] = useMutationWithErrorHandling<PaymentAbortOrderMutation, PaymentAbortOrderMutationVariables>(
    PaymentAbortOrderDocument,
    {
      variables: { orderId: orderId as string },
    },
  )

  const [trackCheckoutComplete] = usePaymentTrackCheckoutCompleteMutation()

  const [getMarketplaceFee, { data: marketplaceFeeData }] = usePaymentGetMarketplaceFeeLazyQuery()

  const { handleGooglePurchaseTracking } = useGooglePurcahseTracking()

  const trackGooglePurchase = async ({ orderId }: { orderId: string }) => {
    await handleGooglePurchaseTracking({ orderId })
    setHasHandledGoogleTracking(true)
  }

  /** **************************************************************************/
  /* Function definitions for payment logic usage                             */
  /** **************************************************************************/

  const handleSelectedPaymentMethodChange = (updatedSelectedPaymentMethod: SelectedPaymentMethod) => {
    const { type: updatedPaymentType, cardId } = updatedSelectedPaymentMethod

    const isPreviousSelectedPaymentMethodNonCreditCard =
      selectedPaymentMethod && selectedPaymentMethod.type !== PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD

    if (cardId && isPreviousSelectedPaymentMethodNonCreditCard) {
      updatedSelectedPaymentMethod.type = PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD
    } else if (cardId && updatedPaymentType !== PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD) {
      updatedSelectedPaymentMethod.cardId = undefined
      updatedSelectedPaymentMethod.saveCard = []
    }

    setSelectedPaymentMethod(updatedSelectedPaymentMethod)
  }

  const pollPaymentStatus = async () => {
    // source payments have the payment context (`lastPaymentAttempt`) attached to the returnUrl as we have to keep the during the `redirect`
    const { wasSuccessful, error } = await pollPayment({ apolloClient }, { orderId: orderId!, lastPaymentAttempt })

    if (wasSuccessful) {
      setPaymentWasSuccessful(true)
    } else if (error) {
      context.setIsPaymentProcessing(false)
      handlePaymentError(error, context.setError, intl)
    }
  }

  /** **************************************************************************/
  /* UI effects synchronization functions                                     */
  /** **************************************************************************/

  // Once the marketplace fee data is returned, we can log the mpf
  useEffect(() => {
    if (!marketplaceFeeData) return

    if (marketplaceFeeData.getMarketplaceFeeForOrder) {
      window.dataLayer.push({
        event: 'phoenix-mpf-tracking',
        mpfv: marketplaceFeeData.getMarketplaceFeeForOrder.value / 100,
        mpfc: marketplaceFeeData.getMarketplaceFeeForOrder.currency,
      })
    }
  }, [marketplaceFeeData])

  // Once the the google event is logged, we can push the user to the confirmation page
  useEffect(() => {
    if (!hasHandledGoogleTracking || !marketplaceFeeData) return
    context.setIsPaymentProcessing(false)
    history.push({ pathname: context.getNextStep(), search: location.search })
  }, [hasHandledGoogleTracking, marketplaceFeeData])

  // Sync UI when coming back from an external payment provider
  useEffect(() => {
    if (paymentInProgress) {
      context.setIsPaymentProcessing(true)
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      pollPaymentStatus()
    }
  }, [])

  // Sync processing payment function according to the selected payment method
  useEffect(() => {
    if (!orderData || !selectedPaymentMethod) return

    if (!orderData.order.payment) {
      // If offline payment is the only available payment method
      if (orderData.order.isConfirmable) {
        context.setProcessPayment(() => async () => confirmOrder({ apolloClient }, orderId!))
      }

      return
    }

    const isMembershipBeingBought = orderData.order.items.some(
      (item) =>
        item.__typename === 'AssignedProductOrderItem' && item.assignedProduct.__typename === 'AssignedMembership',
    )

    // We always wanna make sure that whenever we are buying a membership with a
    // credit card, we have the save card option set to true
    if (isMembershipBeingBought && selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD) {
      selectedPaymentMethod.saveCard = ['true']
    }

    const paymentToProcess: ProcessPaymentOptions = {
      selectedPaymentMethod,
      payment: orderData.order.payment,
      orderId: orderId!,
      user: orderData.order.payment.user,
      ibanElement: elements ? elements.getElement(IbanElement) : null,
      cardElement: elements ? elements.getElement(CardElement) : null,
      idealBankElement: elements ? elements.getElement(IdealBankElement) : null,
      verifyWith,
    }

    // Setting a function in state with useState() needs to be handled this way
    // https://stackoverflow.com/a/55621325
    context.setProcessPayment(() => async () => processPayment({ stripe }, { apolloClient }, paymentToProcess))
  }, [selectedPaymentMethod, stripe])

  // Sync UI when data is fetched from the server
  useEffect(() => {
    if (!orderData) return

    if (!orderData.order.payment) {
      // If offline payment is the only available payment method
      if (orderData.order.isConfirmable) {
        context.setIsPaymentValid(true)
        setSelectedPaymentMethod({ type: CustomPaymentMethodType.PAYMENT_METHOD_OFFLINE, saveCard: [] })
        setPaymentMethods([OFFLINE_PAYMENT_METHOD])
      } else {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        abortOrder()
        throw new BlockingError('There was no payment method available', intl.noPaymentMethodAvailable())
      }
      return
    }

    const { storedPaymentMethods } = orderData.order.payment
    const availablePaymentMethods = orderData.order.payment.paymentMethods.filter(isSupportedPaymentMethodType)

    const savedCard = storedPaymentMethods.find(isStoredCreditCard)
    const hasNoOnlinePayments = availablePaymentMethods.length === 0
    const hasCreditCardPayment = availablePaymentMethods.some(
      (p) => p.type === PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD,
    )
    const isMembershipBeingBought = orderData.order.items.some(
      (item) =>
        item.__typename === 'AssignedProductOrderItem' && item.assignedProduct.__typename === 'AssignedMembership',
    )

    // Pre-select ideal payment if its available as a payment method and the venue is from NL
    const hasIdealPayment = availablePaymentMethods.some((p) => p.type === PaymentMethodType.PAYMENT_METHOD_IDEAL)
    const hasSepaOnlineWithIdealPayment = availablePaymentMethods.some(
      (p) => p.type === PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE && p.verifyWith === 'PAYMENT_METHOD_IDEAL',
    )
    const isNLVenue = orderData.order.venue.location.country === 'NL'
    const isIdealPreselected = isNLVenue && (hasIdealPayment || hasSepaOnlineWithIdealPayment) && !savedCard

    if (isIdealPreselected) {
      setSelectedPaymentMethod({
        type: hasIdealPayment ? PaymentMethodType.PAYMENT_METHOD_IDEAL : PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE,
        saveCard: [],
      })
    } else if (hasCreditCardPayment) {
      setSelectedPaymentMethod({
        type: PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD,
        cardId: savedCard ? savedCard.pspId : undefined,
        saveCard: isMembershipBeingBought ? ['true'] : [],
        countryIsoCode: orderData.order.venue.location.country,
      })
    } else if (hasNoOnlinePayments && orderData.order.isConfirmable) {
      setSelectedPaymentMethod({ type: CustomPaymentMethodType.PAYMENT_METHOD_OFFLINE, saveCard: [] })
    }

    const sepaPaymentMethod = availablePaymentMethods.find(
      ({ type }) => type === PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE,
    )

    if (sepaPaymentMethod && sepaPaymentMethod.verifyWith) {
      setVerifyWith(sepaPaymentMethod.verifyWith)
    }

    if (!isIdealPreselected && (savedCard || hasNoOnlinePayments)) {
      context.setIsPaymentValid(true)
    } else {
      context.setIsPaymentValid(false)
    }

    const initialPaymentMethods: Array<PaymentMethod> = orderData.order.payment ? availablePaymentMethods : []

    if (orderData.order.isConfirmable) {
      setPaymentMethods([...initialPaymentMethods, OFFLINE_PAYMENT_METHOD])
    } else {
      setPaymentMethods(initialPaymentMethods)
    }
  }, [orderData])

  useEffect(() => {
    if (!paymentWasSuccessful) return
    const { isTrackingAllowed } = getUserTrackingPreferences()

    if (!isTrackingAllowed && !isAdvertisingTrackingAllowed) {
      history.push({ pathname: context.getNextStep(), search: location.search })
      return
    }

    if (paymentWasSuccessful && orderId && isTrackingAllowed) {
      if (deviceId && sessionId && orderId) {
        trackCheckoutComplete({
          variables: { orderId, deviceId, sessionId: Number(sessionId) },
        })
      }
      getMarketplaceFee({ variables: { orderId } })
    }

    // track the google purchase event
    const hasGlParam = window.location.href.includes('_gl')
    if (hasGlParam && isAdvertisingTrackingAllowed && !hasHandledGoogleTracking && orderId) {
      trackGooglePurchase({ orderId })
    } else {
      setHasHandledGoogleTracking(true)
    }
  }, [paymentWasSuccessful, amplitude])

  if (isOrderLoading || !orderData || !selectedPaymentMethod) {
    return <SkeletonPayment />
  }

  const { order } = orderData

  return (
    <PaymentForm
      onSelectedPaymentMethodChange={handleSelectedPaymentMethodChange}
      selectedPaymentMethod={selectedPaymentMethod}
      storedPaymentMethods={order.payment ? order.payment.storedPaymentMethods : []}
      paymentMethods={paymentMethods}
      venueMandate={order.payment ? order.payment.mandate : null}
      verifyWith={verifyWith}
      isOrderConfirmed={orderData.order.state === OrderState.ORDER_STATE_CONFIRMED}
      isPaymentProcessing={context.isPaymentProcessing}
    />
  )
}

export default Payment
